import React, { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Dialog, DialogContent, Grid } from "@mui/material";
import { AppointmentItemBox, AppointmentTimelineBox, Transition } from "@icoach/landingPage/dialog/AppointmentFormDialog";
import GradientColorHeader from "@icoach/components/dialog/GradientColorHeader";
import { AppointmentGreetingBox } from "@icoach/landingPage/appointment/";
import { PEOPLE_TYPE } from "@icoach/constants/";
import { AllFaType } from "@icoach/constants/appConfig";
import { AutocompleteField, Button, Divider, Stack, TextField, Typography, Alert, Box } from "@curves/ui-base";
import usePeopleApi from "@apis/usePeopleApi";
import useAppointmentApi from "@apis/useAppointmentApi";
import { refIsRequiredError, isEmpty, isEqual, isFunction } from "@util/utils";
import useMessageDialog from "@util/hook/useMessageDialog";
import { ReadTextField, EmployeeSelect } from "@icoach/components";

// Figure Analysis（體態分析）
// FA入會約運動 顧客預約
const AppointmentPeopleInformationBox = React.forwardRef((props, ref) => {
	const { options: employeeOptions, sourceData, isEdit, onSetAppointmentButtonDisable } = props;
	const { peopleID = null, employeeID = null, notes = null } = sourceData;
	const { getPeopleApi } = usePeopleApi();
	const [customerOptions, setCustomerOptions] = useState([]);
	const peopleRef = useRef(null);
	const employeeRef = useRef(null);
	const appointmentGreetingRef = React.useRef(null);
	const noteRef = useRef(null);

	const getAllCustomerApi = async () => {
		const isEditCutomer = isEdit ? PEOPLE_TYPE.CUSTOMER : PEOPLE_TYPE.FACUSTOMER;
		const resp = await getPeopleApi(isEditCutomer);
		if (resp) setCustomerOptions(resp);
	};

	useImperativeHandle(
		ref,
		() => {
			return {
				getResult: () => {
					return {
						peopleType: PEOPLE_TYPE.CUSTOMER,
						peopleID: peopleID || peopleRef.current.getResult("id"),
						employeeID: employeeRef.current.getResult(),
						notes: noteRef.current.getResult(),
						...(appointmentGreetingRef.current?.getResult() ?? {}),
					};
				},
				isError: () => {
					return refIsRequiredError(peopleRef, employeeRef, noteRef, appointmentGreetingRef);
				},
			};
		},
		// eslint-disable-next-line
		[],
	);

	useEffect(
		() => {
			if (!isEmpty(customerOptions)) {
				onSetAppointmentButtonDisable(false);
			}
		},
		// eslint-disable-next-line
		[customerOptions],
	);

	useEffect(
		() => {
			getAllCustomerApi();
		},
		// eslint-disable-next-line
		[],
	);

	return (
		<React.Fragment>
			<Box className="pt-4">
				<Typography variant="h4" component="h4">
					預約者資料
				</Typography>
			</Box>
			<Grid container spacing={1}>
				<Grid xs={12} item>
					{isEdit ? (
						<ReadTextField label="顧客">{`${sourceData.name},${sourceData.cellPhone}`}</ReadTextField>
					) : (
						<React.Fragment>
							{isEmpty(customerOptions) ? (
								<ReadTextField label="顧客" textProps={{ className: "text-error-dark" }}>
									無FA入會約運動顧客，無法預約
								</ReadTextField>
							) : (
								<AutocompleteField
									label="顧客"
									defaultValue={peopleID}
									ref={peopleRef}
									options={customerOptions}
									optionKeys={["name", "mobile"]}
									optionMaskKeys={["maskName", "maskMobile"]}
									primaryKey="id"
									fullWidth
									InputProps={{ disabled: !!peopleID }}
									required
								/>
							)}
						</React.Fragment>
					)}
				</Grid>
				<Grid xs={12} item>
					{isEdit && AllFaType.some(t => isEqual(t, sourceData.appointmentType)) && <ReadTextField label="來源">{sourceData.sourceTypeText}</ReadTextField>}
				</Grid>
				<Grid xs={6} item>
					<EmployeeSelect ref={employeeRef} options={employeeOptions} defaultValue={employeeID} required fullWidth />
				</Grid>
				<Grid xs={6} item>
					{!isEdit && <AppointmentGreetingBox ref={appointmentGreetingRef} />}
				</Grid>
				<Grid xs={12} item>
					<TextField label="備註" defaultValue={notes} ref={noteRef} multiline fullWidth rows={4} />
				</Grid>
			</Grid>
		</React.Fragment>
	);
});

// 顧客 FA預約用
const AppointmentFigureAnalysisFormDialog = ({ open: openProps, onClose: onCloseProps, refresh: refreshProps, sourceData: sourceDataProps }) => {
	const { initialData, options: optionsProps, isEdit: isEditProps = false } = sourceDataProps || {};
	const {
		appointmentID,
		appointmentDate = null,
		appointmentType = null,
		peopleID = null,
		employeeID = null,
		notes = null,
		name = null,
		cellPhone = null,
		peopleType = null,
		sourceTypeText = "",
	} = initialData || {};
	const setMessageDialog = useMessageDialog();
	const { postAppointmentAddApi, putAppointmentEditApi, postAppointmentCheckApi, getAppointmentFormOptionsDataApi } = useAppointmentApi();
	const apoTypeRef = useRef(null);
	const apoInfoRef = useRef(null);
	const apoTimelineRef = useRef(null);
	const [options, setOptions] = useState({});
	const [disabled, setDisabled] = useState(true); // 入會約運動顧客，新增預約無清單時使用

	const { appointmentCustomerFaTypeOption: customerFaOptions = [], appointmentCustomerTypeOption: workoutOptions = [], employeeOption: employeeOptions = [] } = options;

	const handleAddAppointmentForm = () => {
		if (!refIsRequiredError(apoTypeRef, apoInfoRef, apoTimelineRef)) {
			let params = {};
			const apoParam = apoTypeRef.current.getResult();
			const infoParam = apoInfoRef.current.getResult();
			const apoTimelineParam = apoTimelineRef.current.getResult();
			Object.assign(params, apoParam, infoParam, apoTimelineParam);
			postAppointmentApi(params);
		}
	};

	const handleEditAppointmentForm = () => {
		if (!refIsRequiredError(apoTypeRef, apoInfoRef, apoTimelineRef)) {
			let params = {};
			const apoParam = apoTypeRef.current.getResult();
			const infoParam = apoInfoRef.current.getResult();
			const apoTimelineParam = apoTimelineRef.current.getResult();
			Object.assign(params, apoParam, infoParam, apoTimelineParam);
			putAppointmentApi(params, appointmentID);
		}
	};

	const handleOnclose = () => {
		onCloseProps && onCloseProps();
	};

	const handleSetAppointmentButtonDisable = value => {
		setDisabled(value);
	};

	// 新增預約單
	const postAppointmentApi = async params => {
		const isApo = await postAppointmentCheckApi({
			peopleID: params.peopleID,
			peopleType: params.peopleType,
		});
		if (isApo) {
			if (Array.isArray(isApo) && AllFaType.some(t => isEqual(t, appointmentType))) {
				setMessageDialog({
					open: true,
					title: "預約單訊息",
					MsgComp: () => (
						<Alert color="info" variant="filled" severity="warning">
							<Typography>
								此顧客已預約FA
								<br />
								已預約的日期：
								<br />
								{isApo.map(dateString => (
									<Typography key={dateString} className="pl-5" variant="h5" component="span">
										{dateString}
										<br />
									</Typography>
								))}
							</Typography>
							<Typography>確定要新增預約？？</Typography>
						</Alert>
					),
					onAsyncConfirm: async (e, closeFn) => {
						const resp = await postAppointmentAddApi(params);
						if (resp) {
							if (isFunction(refreshProps)) {
								refreshProps();
							} else if (isFunction(refreshProps?.current)) {
								refreshProps.current();
							}
							handleOnclose();
							closeFn();
						}
					},
				});
			} else {
				const resp = await postAppointmentAddApi(params);
				if (resp) {
					if (isFunction(refreshProps)) {
						refreshProps();
					} else if (isFunction(refreshProps?.current)) {
						refreshProps.current();
					}
					handleOnclose();
				}
			}
		}
	};

	// 修改預約單
	const putAppointmentApi = async (params, apoID) => {
		const resp = await putAppointmentEditApi(params, apoID);
		if (resp) {
			if (isFunction(refreshProps)) {
				refreshProps();
			} else if (isFunction(refreshProps?.current)) {
				refreshProps.current();
			}
			handleOnclose();
		}
	};

	let customerAppointmentTypeOption = [
		{
			text: "WO",
			value: "wo",
			options: workoutOptions,
		},
	];

	if (isEditProps && AllFaType.some(t => isEqual(t, appointmentType))) {
		customerAppointmentTypeOption.push({ text: "銷售", value: "salse_fa", options: customerFaOptions });
	}

	// 初始資料
	const getAppointmentItemDataApi = async () => {
		const resp = await getAppointmentFormOptionsDataApi();
		resp && setOptions(resp);
	};

	useEffect(
		() => {
			if (openProps && isEmpty(options)) {
				if (isEmpty(optionsProps)) {
					getAppointmentItemDataApi();
				} else {
					setOptions(optionsProps);
				}
			}
		},
		// eslint-disable-next-line
		[options, optionsProps, openProps],
	);
	return (
		<Dialog fullScreen open={openProps} TransitionComponent={Transition}>
			<GradientColorHeader onClose={handleOnclose}>{isEditProps ? `編輯預約單` : `FA入會約運動`}</GradientColorHeader>
			{!isEmpty(options) ? (
				<DialogContent>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<AppointmentItemBox ref={apoTypeRef} sourceData={{ item: appointmentType, peopleType }} options={customerAppointmentTypeOption} isEdit={isEditProps} />
							<Divider />
							<AppointmentPeopleInformationBox
								isEdit={isEditProps}
								ref={apoInfoRef}
								options={employeeOptions}
								onSetAppointmentButtonDisable={handleSetAppointmentButtonDisable}
								sourceData={{
									peopleID,
									employeeID,
									notes,
									name,
									cellPhone,
									appointmentType,
									sourceTypeText,
								}}
							/>
						</Grid>
						<Grid item xs={6}>
							<Stack direction="column" spacing={2}>
								<Typography variant="h4" component="h4">
									選擇時間
								</Typography>
								<AppointmentTimelineBox ref={apoTimelineRef} apoDate={appointmentDate} />
								<Stack justifyContent="end">
									{isEditProps ? (
										<Button variant="contained" onClick={handleEditAppointmentForm}>
											確認編輯
										</Button>
									) : (
										<Button variant="contained" onClick={handleAddAppointmentForm} disabled={disabled}>
											確認新增
										</Button>
									)}
								</Stack>
							</Stack>
						</Grid>
					</Grid>
				</DialogContent>
			) : null}
		</Dialog>
	);
};

export default AppointmentFigureAnalysisFormDialog;
